import {
    IconAdjustments,
    IconRectangle,
    IconRectangleVertical,
    IconWand,
} from '@tabler/icons-react';
import { useTranslation } from '../../../../../i18n/hooks/useTranslation';
import Hint from '../../../../../components/common/Hint';
import { toBnDigits } from '../../../../../utils/bnFormat';
import {
    MARGIN_OPTIONS,
    ORIENTATION_OPTIONS,
    PAGE_SIZE_OPTIONS,
} from './createPdfEngine';

// Same glyphs as Image → PDF, so the two tools read alike with the labels gone.
const ORIENTATION_ICONS = {
    auto: IconWand,
    portrait: IconRectangleVertical,
    landscape: IconRectangle,
};


function segBtnClass(active) {
    return `flex-1 min-h-[36px] rounded-lg border-none px-2 py-2 text-[12.5px] transition-all ${
        active
            ? 'bg-white font-semibold text-pdf-cat-solid shadow-[0_1px_2px_rgba(28,24,21,0.04)] dark:bg-zinc-900 dark:text-orange-400'
            : 'bg-transparent font-medium text-[#6B635C] dark:text-zinc-400'
    }`;
}

// Real sheet sizes (mm), for the tooltips and the size note.
const PAGE_SIZE_MM = {
    a4: { w: 210, h: 297 },
    letter: { w: 216, h: 279 },
    a3: { w: 297, h: 420 },
    legal: { w: 216, h: 356 },
    a5: { w: 148, h: 210 },
};

/**
 * A document sheet — folded corner, a few text lines — shaped to the paper's own
 * proportions inside a fixed box, so Legal reads taller and Letter wider than A4
 * while every button keeps the same size. The name sits underneath.
 */
function PageSizeIcon({ id }) {
    const size = PAGE_SIZE_MM[id] || PAGE_SIZE_MM.a4;
    const boxW = 22;
    const boxH = 28;
    const ratio = size.w / size.h;
    const h = boxH - 2;
    const w = Math.min(boxW - 2, h * ratio);
    const x = (boxW - w) / 2;
    const y = 1;
    const fold = 5;
    const lineX1 = x + 3.5;
    const lineX2 = x + w - 3.5;
    return (
        <svg width={boxW} height={boxH} viewBox={`0 0 ${boxW} ${boxH}`} aria-hidden="true">
            <path
                d={`M${x + 2} ${y} H${x + w - fold} L${x + w} ${y + fold} V${y + h - 2} a2 2 0 0 1 -2 2 H${x + 2} a2 2 0 0 1 -2 -2 V${y + 2} a2 2 0 0 1 2 -2 Z`}
                fill="currentColor"
                fillOpacity="0.08"
                stroke="currentColor"
                strokeWidth="1.4"
                strokeLinejoin="round"
            />
            <path
                d={`M${x + w - fold} ${y} V${y + fold - 1} a1 1 0 0 0 1 1 H${x + w}`}
                fill="none"
                stroke="currentColor"
                strokeWidth="1.4"
                strokeLinejoin="round"
            />
            {[10, 14, 18].map((ly) => (
                <line
                    key={ly}
                    x1={lineX1}
                    x2={ly === 18 ? (lineX1 + lineX2) / 2 : lineX2}
                    y1={ly}
                    y2={ly}
                    stroke="currentColor"
                    strokeWidth="1.2"
                    strokeLinecap="round"
                    opacity="0.55"
                />
            ))}
        </svg>
    );
}

function SectionHeader({ title, hint }) {
    return (
        <div className="mb-2 flex items-center justify-between gap-2">
            <p className="text-[12.5px] font-semibold text-[#3D3833] dark:text-zinc-200">{title}</p>
            {hint ? (
                <span className="text-[10.5px] font-medium tracking-wide text-[#A8A099] dark:text-zinc-500">
                    {hint}
                </span>
            ) : null}
        </div>
    );
}

function MarginIcon({ inset }) {
    const pad = { none: 1, small: 3, medium: 5, large: 7 }[inset] ?? 5;
    return (
        <svg width="22" height="28" viewBox="0 0 22 28" aria-hidden="true">
            <rect x="1" y="1" width="20" height="26" rx="2" fill="none" stroke="currentColor" strokeWidth="1.2" />
            <rect x={pad} y={pad + 2} width={22 - pad * 2} height={26 - pad * 2 - 2} rx="1" fill="currentColor" opacity="0.18" />
        </svg>
    );
}

export default function CreatePdfSettings({
    settings,
    onChange,
}) {
    const { t } = useTranslation();

    const update = (patch) => onChange({ ...settings, ...patch });
    const pageSizeLabel = (id) => {
        const mm = PAGE_SIZE_MM[id];
        const name = t(`createPdf.settings.pageSizes.${id}`);
        return mm ? `${name} · ${toBnDigits(mm.w)}×${toBnDigits(mm.h)} mm` : name;
    };

    return (
        <div className="flex flex-col rounded-[20px] border border-[#ECE8E4] bg-white shadow-[0_1px_2px_rgba(28,24,21,0.04)] dark:border-zinc-700 dark:bg-zinc-900">
            <div className="border-b border-[#ECE8E4] px-4 py-3.5 dark:border-zinc-700">
                <div className="flex items-start gap-2.5">
                    <div className="flex h-9 w-9 flex-none items-center justify-center rounded-xl bg-pdf-cat-tint text-pdf-cat-solid dark:bg-orange-950/30 dark:text-orange-400">
                        <IconAdjustments size={18} stroke={1.8} />
                    </div>
                    <div className="min-w-0">
                        <p className="text-[13px] font-semibold text-[#3D3833] dark:text-zinc-200">
                            {t('createPdf.settings.title')}
                        </p>
                        <p className="text-[11.5px] text-[#A8A099] dark:text-zinc-500">
                            {t('createPdf.settings.subtitle')}
                        </p>
                    </div>
                </div>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3.5 [scrollbar-width:thin]">
                <div className="space-y-4">
                    <div>
                        <SectionHeader
                            title={t('createPdf.settings.orientationTitle')}
                        />
                        <div className="grid grid-cols-3 gap-1.5 rounded-[11px] bg-[#F7F5F3] p-1.5 dark:bg-zinc-800">
                            {/* Icons only — the name is in the tooltip and read out
                                via aria-label. */}
                            {ORIENTATION_OPTIONS.map((id) => {
                                const Icon = ORIENTATION_ICONS[id] || IconWand;
                                const label = t(`createPdf.settings.orientations.${id}`);
                                return (
                                    <Hint label={label} key={id}>
                                        <button
                                            type="button"
                                            onClick={() => update({ orientation: id })}
                                            aria-label={label}
                                            aria-pressed={settings.orientation === id}
                                            className={segBtnClass(settings.orientation === id)}
                                        >
                                            <span className="inline-flex items-center justify-center">
                                                <Icon size={18} stroke={1.8} />
                                            </span>
                                        </button>
                                    </Hint>
                                );
                            })}
                        </div>
                    </div>

                    <div>
                        <SectionHeader
                            title={t('createPdf.settings.pageSizeTitle')}
                            hint={pageSizeLabel(settings.pageSize)}
                        />
                        <div className="grid grid-cols-5 gap-1.5 rounded-[11px] bg-[#F7F5F3] p-1.5 dark:bg-zinc-800">
                            {/* Each sheet keeps its paper's proportions; the exact size
                                is in the tooltip. */}
                            {PAGE_SIZE_OPTIONS.map((id) => {
                                const label = pageSizeLabel(id);
                                return (
                                    <Hint label={label} key={id}>
                                        <button
                                            type="button"
                                            onClick={() => update({ pageSize: id })}
                                            aria-label={label}
                                            aria-pressed={settings.pageSize === id}
                                            className={`${segBtnClass(settings.pageSize === id)} flex flex-col items-center justify-center gap-1 py-2`}
                                        >
                                            <PageSizeIcon id={id} />
                                            <span className="text-[10.5px] font-semibold leading-none">
                                                {t(`createPdf.settings.pageSizes.${id}`)}
                                            </span>
                                        </button>
                                    </Hint>
                                );
                            })}
                        </div>
                    </div>

                    <div>
                        <SectionHeader
                            title={t('createPdf.settings.marginTitle')}
                        />
                        <div className="grid grid-cols-4 gap-2">
                            {/* Glyphs only — each draws its own margin, and the
                                name is in the tooltip / aria-label. */}
                            {MARGIN_OPTIONS.map((id) => (
                                <Hint label={t(`createPdf.settings.margins.${id}`)} key={id}>
                                    <button
                                        type="button"
                                        onClick={() => update({ margin: id })}
                                        aria-label={t(`createPdf.settings.margins.${id}`)}
                                        aria-pressed={settings.margin === id}
                                        className={`flex flex-col items-center gap-1 rounded-xl border px-2 py-2 transition-colors ${
                                            settings.margin === id
                                                ? 'border-pdf-cat-solid bg-pdf-cat-tint text-pdf-cat-solid dark:border-orange-500 dark:bg-orange-950/30 dark:text-orange-400'
                                                : 'border-[#ECE8E4] text-[#6B635C] hover:border-pdf-cat-solid/30 dark:border-zinc-700 dark:text-zinc-400'
                                        }`}
                                    >
                                        <MarginIcon inset={id} />
                                    </button>
                                </Hint>
                            ))}
                        </div>
                    </div>

                    <div className="flex items-center justify-between gap-3 rounded-xl border border-[#ECE8E4] bg-[#FAFAF9] px-3 py-3 dark:border-zinc-700 dark:bg-zinc-800/50">
                        <div className="min-w-0 flex-1">
                            <p className="text-[12.5px] font-semibold text-[#3D3833] dark:text-zinc-200">
                                {t('createPdf.settings.pageNumbersTitle')}
                            </p>
                            <p className="text-[11px] text-[#A8A099] dark:text-zinc-500">
                                {t('createPdf.settings.pageNumbersHint')}
                            </p>
                        </div>
                        <button
                            type="button"
                            role="switch"
                            aria-checked={settings.pageNumbers}
                            onClick={() => update({ pageNumbers: !settings.pageNumbers })}
                            className={`relative h-6 w-11 shrink-0 rounded-full transition-colors ${
                                settings.pageNumbers
                                    ? 'bg-pdf-cat-solid dark:bg-orange-500'
                                    : 'bg-[#ECE8E4] dark:bg-zinc-700'
                            }`}
                        >
                            <span
                                className={`absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${
                                    settings.pageNumbers ? 'translate-x-5' : 'translate-x-0'
                                }`}
                            />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
